import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { debounceTime, distinctUntilChanged } from 'rxjs'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatOption } from '@angular/material/core'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { PurchaseServiceOrderService } from '../../../core/services/purchase-reservation/purchase-service-order/purchase-service-order.service'
import { SupplierService } from '../../../core/services/person/supplier/supplier.service'
import { AuthService } from '../../../core/services/auth/auth.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { CurrencyInputDirective } from '../../../shared/directives/currencyInput'
import { convertCurrency } from '../../../shared/utils/utils'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { EditPurchaseServiceOrderBatchModel } from '../../../core/models/purchase-reservation.model'

const GESTOR_RESERVA_COMPRAS_PERFIL_ID = 13

@Component({
  selector: 'purchase-reservation-edit-batch-modal',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOption,
    MatCheckboxModule,
    CurrencyInputDirective,
  ],
  templateUrl: './purchase-reservation-edit-batch-modal.component.html',
  styleUrl: './purchase-reservation-edit-batch-modal.component.scss',
  standalone: true,
})
export class PurchaseReservationEditBatchModalComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private comprasOrdemServicoService: PurchaseServiceOrderService,
    private supplierService: SupplierService,
    private authService: AuthService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<PurchaseReservationEditBatchModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { comprasOsIds: string[]; itens?: any[] },
  ) {
    this.comprasOsIds = data.comprasOsIds
    this.itens = data.itens ?? []
  }

  @Output() response = new EventEmitter()

  comprasOsIds: string[] = []
  itens: any[] = []
  saving = false
  suppliers: any[] = []
  form: FormGroup = new FormGroup({})
  supplierFilterForm: FormGroup = new FormGroup({})

  get isGestor(): boolean {
    return (
      this.authService.getInfosObject()?.idPerfil ==
      GESTOR_RESERVA_COMPRAS_PERFIL_ID
    )
  }

  get totalVendaComTaxa(): number | null {
    const valores = this.itens
      .map((item: any) => item.valorVendaComTaxa)
      .filter((valor: any) => valor != null)
    if (valores.length === 0) {
      return null
    }
    return valores.reduce((total: number, valor: number) => total + valor, 0)
  }

  get osSemVenda(): number {
    return this.itens.filter((item: any) => item.valorVendaComTaxa == null)
      .length
  }

  get totalVendaComTaxaDisplay(): string {
    return this.totalVendaComTaxa != null
      ? `${convertCurrency(this.totalVendaComTaxa)}`
      : '-'
  }

  get targetTotalDisplay(): string {
    const venda = this.totalVendaComTaxa
    const multiplicador = this.form.get('multiplicadorVenda')?.value
    if (venda == null || multiplicador == null || multiplicador === '') {
      return '-'
    }
    return `${convertCurrency(venda * multiplicador)}`
  }

  ngOnInit(): void {
    this.createForm()
    this.createSupplierFilterForm()
    this.listSuppliers()
  }

  createSupplierFilterForm(): void {
    this.supplierFilterForm = this.formBuilder.group({
      nome: [''],
      cnpj: [''],
      cidade: [''],
    })

    this.supplierFilterForm.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(() => this.listSuppliers())
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      fornecedorId: ['', Validators.required],
      valorCompra: ['', Validators.required],
      freelancer: [false],
      valorMotorista: [{ value: '', disabled: true }],
      multiplicadorVenda: [{ value: 0.55, disabled: !this.isGestor }],
      observacaoOs: [''],
      obsFornecedorOperador: [''],
    })

    this.form.get('freelancer')?.valueChanges.subscribe((freelancer: boolean) => {
      const control = this.form.get('valorMotorista')
      if (freelancer) {
        control?.enable()
      } else {
        control?.setValue('')
        control?.disable()
      }
    })
  }

  listSuppliers() {
    const { nome, cnpj, cidade } = this.supplierFilterForm.value
    this.supplierService.listSupplier('S', nome, cnpj, cidade).subscribe({
      next: (response: any) => {
        this.suppliers = response.data ?? []
      },
      error: () => {
        this.suppliers = []
      },
    })
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    const freelancer = this.form.get('freelancer')!.value
    const model: EditPurchaseServiceOrderBatchModel = {
      comprasOsIds: this.comprasOsIds,
      fornecedorId: this.form.get('fornecedorId')!.value,
      valorCompra: convertCurrency(
        this.form.get('valorCompra')!.value,
      ) as number,
      freelancer,
      valorMotorista: freelancer
        ? (convertCurrency(this.form.get('valorMotorista')!.value) as number)
        : 0,
      enviarEmail: false,
      observacaoOs: this.form.get('observacaoOs')!.value || null,
      obsFornecedorOperador:
        this.form.get('obsFornecedorOperador')!.value || null,
    }
    if (this.isGestor) {
      model.multiplicadorVenda = this.form.get('multiplicadorVenda')!.value
    }

    this.comprasOrdemServicoService.editComprasOsLote(model).subscribe({
      next: (response: any) => {
        this.saving = false
        const sucesso = response.data?.sucesso ?? []
        const erros = response.data?.erros ?? []
        if (erros.length === 0) {
          this.snackbar.success(
            `${sucesso.length} OS atualizada(s) com sucesso.`,
          )
        } else {
          this.snackbar.warning(
            `${sucesso.length} OS atualizada(s), ${erros.length} falharam: ${erros
              .map((e: any) => e.mensagem)
              .join('; ')}`,
          )
        }
        this.response.emit(true)
        this.close()
      },
      error: (err: ErrorRequestModel) => {
        this.saving = false
        this.snackbar.errorRequest(err)
      },
    })
  }

  close() {
    this.dialogRef.close()
  }
}
